import { useEffect, useState } from "react";
import axiosInstance from "../../../Configuration/axiosInstance";
import SalesTable from "../SalesTable/SalesTable";
import { useNavigate } from "react-router-dom";
import SearchStrip from "../../../Components/SalesOrder/SearchStrip/SearchStrip";
import LoadingSpinner from "../../../Components/Shared/Loader/Loader";

const ScheduledOrders = () => {
  const [orders, setOrders] = useState([]);
  const [custId, setCustID] = useState();
  const [customers, setCustomers] = useState();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [changed, setDataChanged] = useState(false);
  const [date, setDate] = useState();
  const [comments, setComments] = useState();
  const [count, setCount] = useState();
  const [clearFilters, setClearFilters] = useState(false);
  const [size, setSize] = useState(20);
  const [dialogBox, setDialogBox] = useState(false);
  const [msg, setMsg] = useState("");
  const [searchCriteria, setSearchCriteria] = useState({
    date: null,
    custId: null,
  });

  const [orderNumber, setOrderNumber] = useState();
  const tableHeaders = [
    "Order Number",
    "Customer Name",
    "Purchase Order No.",
    "Comments",
    "SKU's",
    "Order Date(dd/mm/yyyy)",
    "Order Type",
    "Order Status",
    "Images",
    "Action",
  ];

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const [ordersResponse, customersResponse] = await Promise.all([
          axiosInstance.get(
            `order?page=${page}&limit=${pageSize}&scheduled=${true}&customer=${
              searchCriteria.custId
            }&date=${searchCriteria.date}&orderNumber=${
              searchCriteria.orderNumber
            }`
          ),
          axiosInstance.get(`customers/allcustomers`),
        ]);

        // ✅ NEW: Filter out completed orders from scheduled orders
        const allScheduledOrders = ordersResponse.data.data || [];
        const activeScheduledOrders = allScheduledOrders.filter((order) => {
          return order.status !== "Completed";
        });

        console.log(
          `📋 Scheduled Orders: ${allScheduledOrders.length} total, ${activeScheduledOrders.length} active (non-completed)`
        );

        setOrders(activeScheduledOrders);
        setCount(activeScheduledOrders.length); // ✅ Update count to reflect filtered results
        setCustomers(customersResponse.data.data);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        console.error("Error fetching scheduled orders:", err);
        alert("Failed to fetch scheduled orders. Please try again.");
      }
    };

    fetchInitialData();
  }, [page, pageSize, changed, searchCriteria]); // ✅ Added searchCriteria to dependencies

  const customerOptions = customers?.map((i) => ({
    value: i._id,
    label: i.businessName,
  }));

  const handleNext = () => {
    setPage((preValue) => preValue + 1);
  };

  const handlePrevious = () => {
    setPage((preValue) => preValue - 1);
  };

  const navigate = useNavigate();

  const updateOrder = (i) => {
    navigate("/salesOrders/updateOrder", { state: i });
  };

  const deleteOrder = async (i) => {
    try {
      if (
        window.confirm(
          "Are you sure you want to delete this scheduled order?"
        )
      ) {
        await axiosInstance.delete(`order/${i}`);
        setDataChanged(!changed);
        console.log(`✅ Scheduled order ${i} deleted successfully`);
      }
    } catch (error) {
      console.error("Error deleting scheduled order:", error);
      alert("Failed to delete order. Please try again.");
    }
  };

  // ✅ NEW: Get status breakdown for scheduled orders
  const getStatusBreakdown = () => {
    const pending = orders.filter((order) => order.status === "Pending").length;
    const assigned = orders.filter((order) => order.status === "Assigned").length;
    const cancelled = orders.filter((order) => order.status === "Cancelled").length;

    return { pending, assigned, cancelled, total: orders.length };
  };

  const statusBreakdown = getStatusBreakdown();

  return (
    <div>
      {/* ✅ ENHANCED: Better header with status info */}
      <div style={{ marginBottom: "1.5rem" }}>
        <h2>📅 Recurring Orders</h2>
        <p style={{ color: "#6c757d", marginBottom: "1rem" }}>
          Active recurring orders that will create duplicates when completed
        </p>

        {/* ✅ NEW: Quick status overview */}
        {!loading && orders.length > 0 && (
          <div
            style={{
              display: "flex",
              gap: "1rem",
              marginBottom: "1rem",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                background: "#f8f9fa",
                padding: "0.5rem 1rem",
                borderRadius: "8px",
                border: "1px solid #e9ecef",
              }}
            >
              <strong>Total:</strong> {statusBreakdown.total}
            </div>
            {statusBreakdown.pending > 0 && (
              <div
                style={{
                  background: "#fff3cd",
                  padding: "0.5rem 1rem",
                  borderRadius: "8px",
                  border: "1px solid #ffeaa7",
                }}
              >
                <strong>Pending:</strong> {statusBreakdown.pending}
              </div>
            )}
            {statusBreakdown.assigned > 0 && (
              <div
                style={{
                  background: "#d1ecf1",
                  padding: "0.5rem 1rem",
                  borderRadius: "8px",
                  border: "1px solid #a6d8e8",
                }}
              >
                <strong>Assigned:</strong> {statusBreakdown.assigned}
              </div>
            )}
            {statusBreakdown.cancelled > 0 && (
              <div
                style={{
                  background: "#f8d7da",
                  padding: "0.5rem 1rem",
                  borderRadius: "8px",
                  border: "1px solid #f1aeb5",
                }}
              >
                <strong>Cancelled:</strong> {statusBreakdown.cancelled}
              </div>
            )}
          </div>
        )}
      </div>

      <SearchStrip
        size={size}
        setSize={setSize}
        setPageSize={setPageSize}
        pageSize={pageSize}
        setPage={setPage}
        setDate={setDate}
        setSearchCriteria={setSearchCriteria}
        date={date}
        custId={custId}
        clearFilters={clearFilters}
        setDataChanged={setDataChanged}
        setClearFilters={setClearFilters}
        setCustID={setCustID}
        changed={changed}
        customerOptions={customerOptions}
        searchCriteria={searchCriteria}
        customers={customers}
        orderNumber={orderNumber}
        setOrderNumber={setOrderNumber}
      />

      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
          }}
        >
          <LoadingSpinner />
        </div>
      ) : orders.length === 0 ? (
        // ✅ NEW: Enhanced empty state
        <div
          style={{
            textAlign: "center",
            padding: "3rem",
            background: "#f8f9fa",
            borderRadius: "8px",
            border: "2px dashed #dee2e6",
            margin: "2rem 0",
          }}
        >
          <h3 style={{ color: "#6c757d", marginBottom: "1rem" }}>
            📅 No Active Scheduled Orders
          </h3>
          <p style={{ color: "#6c757d", marginBottom: "1rem" }}>
            {searchCriteria.custId ||
            searchCriteria.date ||
            searchCriteria.orderNumber
              ? "No scheduled orders match your current search criteria."
              : "There are currently no active scheduled orders in the system."}
          </p>
          <div style={{ fontSize: "0.9rem", color: "#6c757d" }}>
            <p>
              💡 <strong>Tip:</strong> Completed scheduled orders automatically
              create duplicates and are hidden from this view.
            </p>
            <p>
              📝 Create new scheduled orders by checking "Scheduled Order" when
              creating orders.
            </p>
          </div>
          {(searchCriteria.custId ||
            searchCriteria.date ||
            searchCriteria.orderNumber) && (
            <button
              onClick={() => {
                setSearchCriteria({
                  date: null,
                  custId: null,
                  orderNumber: null,
                });
                setCustID(null);
                setDate(null);
                setOrderNumber(null);
                setClearFilters(true);
              }}
              style={{
                marginTop: "1rem",
                padding: "0.5rem 1rem",
                background: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <>
          {/* ✅ NEW: Info banner about filtered view */}
          <div
            style={{
              background: "#e3f2fd",
              border: "1px solid #bbdefb",
              borderRadius: "8px",
              padding: "1rem",
              marginBottom: "1rem",
              fontSize: "0.9rem",
              color: "#1976d2",
            }}
          >
            <strong>ℹ️ Note:</strong> This view shows only active scheduled orders
            (not completed). When a scheduled order is completed, it creates a
            duplicate and is hidden from this list.
          </div>

          <SalesTable
            orders={orders}
            setOrders={setOrders}
            updateOrder={updateOrder}
            deleteOrder={deleteOrder}
            handlePrevious={handlePrevious}
            handleNext={handleNext}
            page={page}
            pageSize={pageSize}
            count={count}
            tableHeaders={tableHeaders}
          />
        </>
      )}
    </div>
  );
};

export default ScheduledOrders;
